import { animate, state, style, transition, trigger } from '@angular/animations'
import { Component, HostListener, OnInit } from '@angular/core'
import { faAddressBook, faAngleDoubleLeft, faBan, faBars, faBoxOpen, faBoxes, faBoxesPacking, faCube, faDatabase, faClockRotateLeft, faCubes, faDollyFlatbed, faFileLines, faGraduationCap, faHome, faKaaba, faMapMarkerAlt, faTableList, faPercent, faRobot, faRss, faTags, faTasks, faTools, faUndo, faUserAstronaut, faUserLock, faUsers, faUserGroup, faWarehouse, faHandshake, faMoneyBills, faGear, faXmark } from '@fortawesome/free-solid-svg-icons'
import { Store } from '@ngrx/store'
import { Observable } from 'rxjs/internal/Observable'
import { ADMIN_GROUP, OPERATOR_GROUP } from '@sauna-net/dto'
import { GROUPS_PRICING } from '../../../constants/pricing'
import { AppState, IAuthState } from '../../../core/reducers/index'
import { environment } from '../../../environments/environment'
import {
  AuthService,
  isAdministrationAllowedUser,
  isOfflineAuthAdminUser,
  isSynchronizerReplayOnlyUser,
  OFFLINE_AUTH_DEFAULT_ROUTE,
} from '../../login/services/auth.service'
import { NGAnimations } from '@sauna-net/client/utils/ng_animations'
import { getAccessUsers, isServicesWarehouseUser } from '../../access/access-config'
import { CAMERAS_ENABLED } from '../../cameras/cameras-availability'
import { LayoutService } from '../../layout/services/layout.service'

@Component({
  selector: 'fs-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  animations: [
    trigger('openClose', [
      state(
        'open',
        style({
          width: '15rem',
        }),
      ),
      state('close', style({ width: '3.7rem' })),
      transition('open <=> close', [animate('0.2s')]),
    ]),
    NGAnimations.SlideUp(),
  ],
})
export class HeaderComponent implements OnInit {
  isPublic = false
  auth$: Observable<IAuthState>
  logo = environment.logo

  open = true
  mobileOpen = false
  tempOpen = true
  showMovingMoravany = false
  readonly camerasEnabled = CAMERAS_ENABLED
  // icons
  faHome = faHome
  faBan = faBan
  faMapMarkerAlt = faMapMarkerAlt
  faUndo = faUndo
  faCube = faCube
  faTools = faTools
  faTimes = faXmark
  faBars = faBars
  faBoxOpen = faBoxOpen
  faBoxesPacking = faBoxesPacking
  faBoxes = faBoxes
  faDollyFlatbed = faDollyFlatbed
  faAngleDoubleLeft = faAngleDoubleLeft
  faAddressBook = faAddressBook
  faTasks = faTasks
  faWarehouse = faWarehouse
  faUsers = faUsers
  faUserGroup = faUserGroup
  faUserAstronaut = faUserAstronaut
  faUserLock = faUserLock
  faTags = faTags
  faPercent = faPercent
  faRobot = faRobot
  faRss = faRss
  faDatabase = faDatabase
  faTableList = faTableList
  faFileLines = faFileLines
  faClockRotateLeft = faClockRotateLeft
  faKaaba = faKaaba
  faCubes = faCubes
  faGraduationCap = faGraduationCap
  faHandshake = faHandshake
  faMoneyBills = faMoneyBills
  faGear = faGear

  private prevWidth = typeof window !== 'undefined' ? window.innerWidth : 1920

  constructor(
    public authService: AuthService,
    private store: Store<AppState>,
    private layoutService: LayoutService,
  ) {
    this.auth$ = this.store.select('auth')
    if (typeof window !== 'undefined') {
      const width = window.innerWidth
      this.prevWidth = width
      if (width <= 1450) {
        this.open = false
      } else {
        const isOpen = localStorage.getItem('NAV_TOGGLE') || '1'
        this.open = isOpen === '1'
      }
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (typeof window === 'undefined') return
    const width = window.innerWidth
    if (width <= 1450 && this.prevWidth > 1450) {
      this.open = false
    } else if (width > 1450 && this.prevWidth <= 1450) {
      const isOpen = localStorage.getItem('NAV_TOGGLE') || '1'
      this.open = isOpen === '1'
    } else if (width <= 1024) {
      this.open = false
    }
    this.prevWidth = width
  }

  ngOnInit(): void {
    this.store.select('router').subscribe(() => {
      if (this.mobileOpen) this.toggleMobileMenu()
    })
    this.layoutService.stage$.subscribe(stage => {
      if (stage >= 1 && this.open) {
        this.open = false
      }
    })
  }

  toggleOpen() {
    this.open = !this.open
    localStorage.setItem('NAV_TOGGLE', this.open ? '1' : '0')
  }

  toggleMobileMenu() {
    this.mobileOpen = !this.mobileOpen
  }

  mouseEnter() {
    if (!this.open) {
      this.tempOpen = this.open
      this.open = true
    }
  }

  mouseLeave() {
    if (!this.tempOpen) {
      this.open = false
      this.tempOpen = true
    }
  }

  get servicesGroups(): number[] {
    return [OPERATOR_GROUP, ADMIN_GROUP]
  }

  canAccessServices(auth?: Pick<IAuthState, 'user'> | null): boolean {
    const user = auth?.user
    if (!user) {
      return false
    }

    const isServicesUser = typeof user.user_id === 'number' && getAccessUsers('servicesAllowedUsers').includes(user.user_id)
    const hasServicesGroup = !!user.groups?.some((group: { id: number }) => this.servicesGroups.includes(group.id))

    return isServicesUser || hasServicesGroup || isServicesWarehouseUser(user)
  }

  get groupsPricing(): number[] {
    return GROUPS_PRICING
  }

  isOfflineAuth(auth?: Pick<IAuthState, 'user'> | null): boolean {
    return auth?.user?.authProvider === 'offline'
  }

  // TODO(access): nahradit za funkci v ACCESS_FEATURES a *fsCanAccess / AccessService (app/access).
  canAccessAdministration(auth?: IAuthState | null): boolean {
    const user = auth?.user

    if (!user) {
      return false
    }

    if (this.isOfflineAuth(auth)) {
      return isOfflineAuthAdminUser(user.user_id)
    }

    return (
      isAdministrationAllowedUser(user.user_id) ||
      isSynchronizerReplayOnlyUser(user.user_id) ||
      !!user.groups?.some((group: { id: number }) => group.id === ADMIN_GROUP)
    )
  }

  isSynchronizerReplayOnly(auth?: IAuthState | null): boolean {
    return !!auth?.user && !this.isOfflineAuth(auth) && isSynchronizerReplayOnlyUser(auth.user.user_id)
  }

  getHomeLink(auth?: Pick<IAuthState, 'user'> | null): string {
    return this.isOfflineAuth(auth) ? OFFLINE_AUTH_DEFAULT_ROUTE : '/'
  }
}
